import { routeDelaysManager } from "@/setup/routes/delay.routes";
import { HoursProgressBar } from "@/shared/components/HoursProgressBar";
import { KPISection } from "@/shared/components/KPISection";
import { pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	useMesfRealtime,
	useHasPermission,
} from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShiftChart } from "../gantt/components/ShiftChart";
import { MillOverview2Skeleton } from "./components/MillOverview2Skeleton";
import { IncentiveTankChart } from "./components/IncentiveTankChart";
import { ProductionChart } from "./components/ProductionChart";
import { RollingTotalStats } from "./components/RollingTotalStats";
import { RollStatusIndicator } from "./components/RollStatusIndicator";
import {
	CurrentSectionInfo,
	UpcomingSectionInfo,
} from "./components/SectionInfo";
import { ShiftStats } from "./components/ShiftStats";
import { RollChangeCountdown } from "./components/RollChangeCountdown";
import { ActiveEventChip } from "@/shared/components/ActiveEventChip";
import { StatusIndicator } from "./components/StatusIndicator";
import { UsageMeters } from "./components/UsageMeters";
import type { MillOverview2Response } from "./models/MillOverview2";
import { getMillOverview2 } from "./repositories/MillOverview2Repository";

const moment = getMomentTz();

export const useMillOverview2 = () => {
	return useQuery<MillOverview2Response, Error>({
		queryKey: ["millOverview2"],
		queryFn: ({ signal }) => getMillOverview2(signal),
		refetchOnWindowFocus: true,
		refetchInterval: 30_000,
	});
};

export const MillOverview2Page = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [error, setError] = useState("");
	const hasPermissionTo = useHasPermission();
	const canViewIncentive = hasPermissionTo(
		"ui.navbar.mill.incentive.dashboard",
	);

	const chartDates = useMemo(() => {
		const end = moment();
		const start = moment();
		return { start: start.toDate(), end: end.toDate() };
	}, []);

	const { data, isLoading, error: e, isError } = useMillOverview2();

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") ===
				"M2.GetMillOverview2"
			) {
				await queryClient.invalidateQueries({ queryKey: ["millOverview2"] });
			}
		},
	});

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	if (isLoading) {
		return <MillOverview2Skeleton />;
	}

	if (isError) {
		return <ErrorModal error={error} onHide={() => setError("")} />;
	}

	return (
		<>
			<HelmetDexteel title="2 Mill Overview" />
			<Box sx={{ p: 1.5, backgroundColor: pageBgColor }}>
				<Grid2 container spacing={1}>
					{/* ── Title row ── */}
					<Grid2 size={{ xs: 12 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
							}}
						>
							<Typography variant="h5" sx={{ fontWeight: 600 }}>
								2 Mill Overview
							</Typography>
							<StatusIndicator
								isInDelay={data.IsInDelay}
								onClick={
									data.IsInDelay && data.CurrentDelayId
										? () =>
												navigate(
													`${routeDelaysManager}?delayId=${data.CurrentDelayId}`,
												)
										: undefined
								}
							/>
							{data.RollChangeCountdown?.ActiveDMHEvents.map((dmh, i) => (
								<ActiveEventChip
									key={`${dmh.EventTypeCode}-${dmh.Start.getTime()}-${i}`}
									eventTypeCode={dmh.EventTypeCode}
									start={dmh.Start}
									remainingMinutes={dmh.RemainingMinutes}
								/>
							))}
							<RollChangeCountdown data={data.RollChangeCountdown} />
						</Box>
					</Grid2>

					{/* ── Top row: 30-day chart + Current Section + Hours Progress Bar ── */}
					<Grid2
						container
						spacing={1}
						size={{ xs: 12 }}
						sx={{ alignItems: "stretch" }}
					>
						<Grid2 size={{ xs: 12, lg: 4 }} sx={{ overflow: "hidden" }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									height: "100%",
									pt: "10px",
								}}
							>
								<Typography
									variant="subtitle2"
									sx={{ fontWeight: 700, mb: 0.5 }}
								>
									Production over last 30 days
								</Typography>
								<ShiftChart
									start={chartDates.start}
									end={chartDates.end}
									grid={{
										top: 10,
										left: 10,
										containLabel: true,
									}}
									cardHeight={130}
									disableOnClick
								/>
							</Box>
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<CurrentSectionInfo data={data.CurrentSection} />
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Paper
								elevation={1}
								sx={{
									p: 1,
									border: "2px solid #4CAF50",
									borderRadius: 2,
									minWidth: 0,
									height: "100%",
								}}
							>
								<HoursProgressBar
									eventStart={data.CurrentSection.EventStart}
									scheduledEnd={data.CurrentSection.ScheduledEnd}
									projectedEnd={data.CurrentSection.ProjectedRollOut}
									scheduledHours={data.CurrentSection.ScheduledHours}
									currentHours={data.CurrentSection.CurrentHours}
									remainingHours={data.CurrentSection.RemainingHours}
									varianceHours={data.CurrentSection.VarianceHours}
								/>
							</Paper>
						</Grid2>
					</Grid2>

					{/* ── Middle row: Rolling Total + Production Chart + (Upcoming & RollStatus card) ── */}
					<Grid2
						container
						spacing={1}
						size={{ xs: 12 }}
						sx={{ alignItems: "stretch" }}
					>
						<Grid2 size={{ xs: 12, lg: 4 }} sx={{ order: { xs: 2, lg: 1 } }}>
							<RollingTotalStats data={data.RollingTotal} />
						</Grid2>
						<Grid2
							size={{ xs: 12, lg: 4 }}
							sx={{ order: { xs: 1, lg: 2 }, display: "flex", gap: 1 }}
						>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<ProductionChart data={data.ProductionChart} />
							</Box>
							{canViewIncentive && (
								<Box sx={{ width: 80, flexShrink: 0 }}>
									<IncentiveTankChart data={data.IncentiveTank} />
								</Box>
							)}
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }} sx={{ order: { xs: 3, lg: 3 } }}>
							<Paper
								elevation={1}
								sx={{
									p: 1,
									border: "2px solid #2196F3",
									borderRadius: 2,
									minWidth: 0,
									height: "100%",
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 0.5,
										height: "100%",
									}}
								>
									<UpcomingSectionInfo data={data.UpcomingSection} />
									<Box
										sx={{
											borderTop: "1px solid #e0e0e0",
											pt: 1,
										}}
									>
										<RollStatusIndicator data={data.StandReadiness} />
									</Box>
								</Box>
							</Paper>
						</Grid2>
					</Grid2>

					{/* ── Shifts + Usage + KPI gauges (aligned by base) ── */}
					<Grid2
						container
						spacing={1}
						size={{ xs: 12 }}
						sx={{ alignItems: "stretch" }}
					>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									height: "100%",
								}}
							>
								<ShiftStats
									title="Current Shift"
									data={data.CurrentShift}
									showTPHIndicator
									showPushStats
								/>
								<ShiftStats
									title="Previous Shift"
									data={data.PreviousShift}
									showTPHIndicator
								/>
								<Box
									sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
								>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 600, mb: 1 }}
									>
										Usage Meters
									</Typography>
									<Box sx={{ flexGrow: 1 }}>
										<UsageMeters data={data.UsageMeters} />
									</Box>
								</Box>
							</Box>
						</Grid2>

						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									height: "100%",
								}}
							>
								<KPISection
									title="Current Shift"
									data={data.CurrentShiftKPI}
									visible={["tph", "uptime"]}
								/>
								<KPISection
									title="Rolling Total"
									data={data.RollingTotalKPI}
									visible={["tph", "uptime"]}
								/>
							</Box>
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									height: "100%",
								}}
							>
								<KPISection
									data={data.CurrentShiftKPI}
									visible={["pushrate", "productivity"]}
								/>
								<KPISection
									data={data.RollingTotalKPI}
									visible={["pushrate", "productivity"]}
								/>
							</Box>
						</Grid2>
					</Grid2>
				</Grid2>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
